import { action, observable } from 'mobx'
import { type Storage } from '@/infra/storage'
import { createObjectStorage, type ObjectStorage } from '@/infra/object-storage'

/**
 * A column whose width a {@link ColumnLayout} tracks.
 */
export interface LayoutColumn {
  id: string
  defaultWidth: number
}

const minColumnWidth = 72
const maxColumnWidth = 720

interface StoredSettings {
  widths?: Record<string, number>
}

/**
 * Column widths for a `DataTable`, remembered per browser under
 * `storageKey`, and whether its selection checkboxes are shown.
 */
export class ColumnLayout {
  private readonly _storage: ObjectStorage
  @observable private accessor _widths: Record<string, number> = {}
  /**
   * Whether the selection checkboxes are shown.
   */
  @observable accessor showSelection = false

  constructor(
    storage: Storage,
    private readonly _storageKey: string,
  ) {
    this._storage = createObjectStorage(storage)
    const stored = (this._storage.getItem(_storageKey) ?? {}) as StoredSettings
    this._widths = stored.widths ?? {}
  }

  /**
   * The column's remembered width, or its default.
   */
  widthOf(column: LayoutColumn) {
    return this._widths[column.id] ?? column.defaultWidth
  }

  /**
   * Sets a column's width within the allowed range, without saving it.
   */
  @action.bound
  setWidth(id: string, width: number) {
    this._widths[id] = Math.round(
      Math.min(maxColumnWidth, Math.max(minColumnWidth, width)),
    )
  }

  /**
   * Shows or hides the selection checkboxes.
   */
  @action.bound
  toggleSelection() {
    this.showSelection = !this.showSelection
  }

  /**
   * Remembers the current widths.
   */
  @action.bound
  save() {
    const settings: StoredSettings = {
      widths: this._widths,
    }
    this._storage.setItem(this._storageKey, settings)
  }
}
